'use client';

import { Loader2, MessageSquare } from 'lucide-react';
import React, { useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useComments,
  useCreateComment,
  useDeleteComment,
  usePinnedComments,
  useTogglePinComment,
  useToggleReaction,
  useUpdateComment,
} from '../../hooks/useComments';
import { useFileUpload } from '../../hooks/useFileUpload';
import { useWorkspaceEvent } from '../../hooks/useWorkspacePresence';
import { trpc } from '../../lib/trpc';
import { cn } from '../../lib/utils';
import type { EditorAttachment, Comment as RichComment, User } from '../../types/comments';
import { Button } from '../../ui/button';
import { FileDropzone, useFileDropzone } from '../file-dropzone';
import { CommentCard } from './comment-card';
import { CommentReplies } from './comment-replies';
import { TiptapEditor } from './tiptap-editor';

interface CommentThreadProps {
  /**
   * The thread to render. `null` means "no thread exists yet" — the feed shows
   * its empty state and the composer stays live so the first message can create
   * the thread lazily via {@link CommentThreadProps.onCreateThread}.
   */
  threadId: number | null;
  users: User[];
  currentUser: User;
  /**
   * Called on the first send when `threadId` is `null`. Must create the backing
   * thread and resolve to its new id; the just-typed message is then posted to
   * it through the normal comment pipeline (mentions, attachments, activity).
   */
  onCreateThread?: () => Promise<number>;
  /** Gates @mention suggestions, mirroring the backend's team-plan gate on comment attachments. */
  variant?: 'overlay' | 'sidebar';
  isReadOnly?: boolean;
  onError?: (message: string) => void;
}

function ConnectedEditor(props: React.ComponentProps<typeof TiptapEditor>) {
  const { openFilePicker } = useFileDropzone();
  return <TiptapEditor {...props} onPaperclipClick={openFilePicker} />;
}

export function CommentThread({
  threadId,
  users,
  currentUser,
  onCreateThread,
  variant = 'sidebar',
  isReadOnly = false,
  onError,
}: CommentThreadProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Guards against a double-create if the user fires two sends before the first
  // thread-creation round-trip resolves.
  const [isCreatingThread, setIsCreatingThread] = useState(false);

  // Hooks
  const { data: pinnedComments = [], isLoading: isLoadingPinned } = usePinnedComments(threadId);
  const {
    data: infiniteData,
    isLoading: isLoadingMain,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useComments(threadId);

  const createCommentMutation = useCreateComment();
  const deleteMutation = useDeleteComment();
  const togglePinMutation = useTogglePinComment();
  const toggleReactionMutation = useToggleReaction();
  const updateCommentMutation = useUpdateComment();

  const { workspaceId: workspaceIdStr } = useParams<{ workspaceId?: string }>();
  const workspaceId = workspaceIdStr ? parseInt(workspaceIdStr, 10) : null;
  const utils = trpc.useUtils();

  useWorkspaceEvent(workspaceId, currentUser.id, (event) => {
    if (threadId == null) return;
    if (event.payload?.threadId === threadId) {
      if (
        event.type === 'COMMENT_CREATED' ||
        event.type === 'COMMENT_UPDATED' ||
        event.type === 'COMMENT_DELETED' ||
        event.type === 'COMMENT_REACTION_TOGGLED'
      ) {
        utils.comments.getForThread.invalidate({ threadId });
        utils.comments.getPinned.invalidate({ threadId });
        if (event.payload?.parentId && typeof event.payload.parentId === 'number') {
          utils.comments.getReplies.invalidate({ parentId: event.payload.parentId });
        } else {
          utils.comments.getReplies.invalidate();
        }
      }
    }
  });

  const handleMutationError = (err: unknown) => {
    const message = err instanceof Error ? err.message : 'An unexpected error occurred';
    onError?.(message);
  };
  const { uploadAll, isUploading } = useFileUpload();

  // Mapping Helper
  const mapToRichComment = React.useCallback(
    (c: {
      id: number;
      threadId: number;
      parentId: number | null;
      content: string;
      reactions: unknown;
      isPinned: boolean;
      hasAttachments: boolean;
      createdBy: number;
      createdAt: number;
    }): RichComment => {
      const author = users.find((u) => u.id === c.createdBy) || {
        id: c.createdBy,
        fullName: c.createdBy === 0 ? 'System' : 'Unknown User',
        avatarUrl: null,
        email: '',
      };

      const reactions = (c.reactions as { emoji: string; userId: number }[]) || [];

      return {
        id: c.id,
        threadId: c.threadId,
        parentId: c.parentId ?? null,
        author,
        createdBy: c.createdBy,
        content: c.content,
        createdAt: c.createdAt,
        reactions,
        isPinned: !!c.isPinned,
        hasAttachments: !!c.hasAttachments,
      };
    },
    [users]
  );

  const allComments = useMemo(() => {
    if (!infiniteData) return [];
    return (infiniteData.pages || [])
      .flatMap((page) => page.items || [])
      .filter((c): c is NonNullable<typeof c> => c !== null)
      .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
      .map(mapToRichComment);
  }, [infiniteData, mapToRichComment]);

  const pinnedRichComments = useMemo(
    () =>
      pinnedComments.filter((c): c is NonNullable<typeof c> => c !== null).map(mapToRichComment),
    [pinnedComments, mapToRichComment]
  );

  // Local State
  const [editorAttachments, setEditorAttachments] = useState<EditorAttachment[]>([]);
  const [removeAssociationIds, setRemoveAssociationIds] = useState<number[]>([]);
  const [editingComment, setEditingComment] = useState<RichComment | null>(null);
  const [replyingToComment, setReplyingToComment] = useState<RichComment | null>(null);
  // Track which comments have their replies expanded
  const [expandedReplies, setExpandedReplies] = useState<Set<number>>(new Set());

  const toggleReplies = (commentId: number) => {
    setExpandedReplies((prev) => {
      const next = new Set(prev);
      if (next.has(commentId)) {
        next.delete(commentId);
      } else {
        next.add(commentId);
      }
      return next;
    });
  };

  // Fetch existing files when editing
  const { data: existingFiles } = trpc.files.getForTarget.useQuery(
    { targetType: 'comment', targetId: editingComment?.id ?? 0 },
    { enabled: !!editingComment }
  );

  // Sync existing files to editor once per edit session (React's documented
  // "adjust state while rendering" pattern — avoids a setState-in-effect
  // render loop while only merging in remote files once they've loaded for
  // the comment currently being edited).
  const [syncedFilesForCommentId, setSyncedFilesForCommentId] = useState<number | null>(null);
  if (existingFiles && editingComment && syncedFilesForCommentId !== editingComment.id) {
    setSyncedFilesForCommentId(editingComment.id);
    const remoteAtts: EditorAttachment[] = existingFiles.map((f) => ({
      type: 'remote',
      fileId: f.id,
      fileName: f.fileName,
      fileType: f.fileType,
      associationId: f.associationId,
      viewUrl: f.viewUrl,
    }));
    setEditorAttachments((prev) => {
      const localOnly = prev.filter((a) => a.type === 'local');
      return [...localOnly, ...remoteAtts];
    });
  }

  // Resets the sync guard too, so re-editing the same comment after
  // cancelling re-triggers the merge above once its files are (re)fetched.
  const closeEditing = () => {
    setEditingComment(null);
    setSyncedFilesForCommentId(null);
  };

  const handleSendComment = async (content: string) => {
    if (isCreatingThread) return;
    try {
      const localFiles = editorAttachments
        .filter((a): a is Extract<EditorAttachment, { type: 'local' }> => a.type === 'local')
        .map((a) => a.file);

      // 1. Upload new files only on submit
      const newFileIds = await uploadAll(localFiles);

      if (editingComment) {
        await updateCommentMutation.mutateAsync(
          {
            id: editingComment.id,
            content: content,
            fileIds: newFileIds,
            removeAssociationIds,
          },
          {
            onError: handleMutationError,
          }
        );
        closeEditing();
      } else {
        // Lazy thread creation: on the first message of an entity that has no
        // thread yet, mint the thread, then post this message to it through the
        // normal comment pipeline so mentions/attachments/activity all fire.
        let targetThreadId = threadId;
        if (targetThreadId == null) {
          if (!onCreateThread) throw new Error('No thread and no way to create one');
          setIsCreatingThread(true);
          try {
            targetThreadId = await onCreateThread();
          } finally {
            setIsCreatingThread(false);
          }
        }

        await createCommentMutation.mutateAsync(
          {
            threadId: targetThreadId,
            content: content,
            fileIds: newFileIds,
            ...(replyingToComment ? { parentId: replyingToComment.id } : {}),
          },
          {
            onError: handleMutationError,
          }
        );

        // Auto-expand replies for the parent when a reply is sent
        if (replyingToComment) {
          setExpandedReplies((prev) => new Set(prev).add(replyingToComment.id));
        }

        setReplyingToComment(null);
      }

      setEditorAttachments([]);
      setRemoveAssociationIds([]);
    } catch (err) {
      console.error('Failed to send comment with attachments:', err);
    }

    // Scroll to bottom after sending
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      }
    }, 100);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (target.scrollTop < 50 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // A null threadId leaves the comment queries disabled, so don't read their
  // (perpetually pending) loading flags — show the empty state + composer.
  const isLoading =
    threadId != null && (isLoadingMain || isLoadingPinned) && allComments.length === 0;

  // Editor mode label
  const activeMode = editingComment ? 'edit' : replyingToComment ? 'reply' : 'create';

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-inherit overflow-hidden">
      {/* Main Feed Container */}
      <div className="flex-1 relative min-h-0 overflow-hidden">
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="h-full overflow-y-auto p-0 flex flex-col gap-1 scrollbar-hide"
        >
          {isFetchingNextPage && (
            <div className="flex justify-center py-2">
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            </div>
          )}

          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground opacity-50">
              <Loader2 className="size-6 animate-spin mb-2" />
              <span className="text-xs text-center">Loading messages...</span>
            </div>
          ) : allComments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground opacity-40">
              <MessageSquare className="size-10 mb-3" />
              <span className="text-sm font-medium">No messages yet</span>
              <span className="text-[11px] text-center max-w-[180px]">
                Be the first to start the conversation
              </span>
            </div>
          ) : (
            <>
              {/* Show pinned comments at the top if they exist */}
              {pinnedRichComments.length > 0 && variant === 'sidebar' && (
                <div className="mb-4 border-b pb-4">
                  <div className="flex items-center gap-2 text-[10px] font-bold text-amber-500 uppercase tracking-widest mb-2 px-1">
                    Pinned
                  </div>
                  <div className="flex flex-col gap-1">
                    {pinnedRichComments.map((c) => (
                      <CommentCard
                        key={`pinned-${c.id}`}
                        comment={c}
                        currentUser={currentUser}
                        users={users}
                        variant={variant}
                        isReadOnly={isReadOnly}
                        onReaction={(id, emoji) =>
                          toggleReactionMutation.mutate(
                            { id, emoji },
                            { onError: handleMutationError }
                          )
                        }
                        onDelete={(id) =>
                          deleteMutation.mutate({ id }, { onError: handleMutationError })
                        }
                        onEdit={(c) => {
                          setReplyingToComment(null);
                          setEditingComment(c);
                        }}
                        onPin={(id) =>
                          togglePinMutation.mutate(
                            { id, isPinned: !c.isPinned },
                            { onError: handleMutationError }
                          )
                        }
                        onReply={(c) => {
                          closeEditing();
                          setReplyingToComment(c);
                        }}
                        repliesOpen={expandedReplies.has(c.id)}
                        onToggleReplies={() => toggleReplies(c.id)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {allComments.map((c) => (
                <div key={c.id} className="flex flex-col">
                  <CommentCard
                    comment={c}
                    currentUser={currentUser}
                    users={users}
                    variant={variant}
                    isReadOnly={isReadOnly}
                    onReaction={(id, emoji) =>
                      toggleReactionMutation.mutate({ id, emoji }, { onError: handleMutationError })
                    }
                    onDelete={(id) =>
                      deleteMutation.mutate({ id }, { onError: handleMutationError })
                    }
                    onEdit={(c) => {
                      setReplyingToComment(null);
                      setEditingComment(c);
                    }}
                    onPin={(id) =>
                      togglePinMutation.mutate(
                        { id, isPinned: !c.isPinned },
                        { onError: handleMutationError }
                      )
                    }
                    onReply={(c) => {
                      closeEditing();
                      setReplyingToComment(c);
                    }}
                    repliesOpen={expandedReplies.has(c.id)}
                    onToggleReplies={() => toggleReplies(c.id)}
                  />
                  {expandedReplies.has(c.id) && (
                    <CommentReplies
                      parentId={c.id}
                      // Comments only exist once the thread does, so threadId is
                      // guaranteed non-null in this branch.
                      threadId={threadId as number}
                      currentUser={currentUser}
                      users={users}
                      variant={variant}
                      isReadOnly={isReadOnly}
                      onReaction={(id, emoji) =>
                        toggleReactionMutation.mutate(
                          { id, emoji },
                          { onError: handleMutationError }
                        )
                      }
                      onDelete={(id) =>
                        deleteMutation.mutate({ id }, { onError: handleMutationError })
                      }
                      onEdit={(c) => {
                        setReplyingToComment(null);
                        setEditingComment(c);
                      }}
                    />
                  )}
                </div>
              ))}
            </>
          )}
        </div>

        {/* Edit Mode Overlay */}
        {editingComment && (
          <div
            className="absolute inset-0 bg-background/60 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center animate-in fade-in duration-300 cursor-default"
            onClick={(e) => {
              e.stopPropagation();
              closeEditing();
            }}
          >
            <div className="bg-background/80 border border-border shadow-xl rounded-full px-4 py-2 flex items-center gap-2 scale-90 opacity-80 select-none">
              <div className="size-2 rounded-full bg-amber-500 animate-pulse" />
              <span className="text-xs font-medium text-muted-foreground tracking-tight">
                Click to exit editing mode
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      {!isReadOnly && (
        <div className="px-0 border-t">
          <FileDropzone
            onFilesDrop={(files) => {
              const newAtts: EditorAttachment[] = files.map((f) => ({
                type: 'local',
                file: f,
                id: Math.random().toString(36).slice(2),
              }));
              setEditorAttachments((prev) => [...prev, ...newAtts]);
            }}
            overlayText="Drop files to attach..."
            className="w-full"
          >
            {/* Editing banner */}
            {editingComment && (
              <div className="mb-2 flex items-center justify-between bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-1.5 animate-in fade-in slide-in-from-bottom-2">
                <div className="flex items-center gap-2">
                  <div className="size-1.5 rounded-full bg-amber-500 animate-pulse" />
                  <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">
                    Editing Message
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    closeEditing();
                    setEditorAttachments([]);
                    setRemoveAssociationIds([]);
                  }}
                  className="h-5 px-1.5 text-[10px] hover:bg-amber-500/20 text-amber-700 font-bold"
                >
                  Cancel
                </Button>
              </div>
            )}

            {/* Replying banner */}
            {replyingToComment && !editingComment && (
              <div className="mb-2 flex items-center justify-between bg-primary/10 border border-primary/20 rounded-md px-3 py-1.5 animate-in fade-in slide-in-from-bottom-2">
                <div className="flex items-center gap-2">
                  <div className="size-1.5 rounded-full bg-primary animate-pulse" />
                  <span className="text-[10px] font-bold text-primary/80 uppercase tracking-wider">
                    Replying to {replyingToComment.author.fullName}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setReplyingToComment(null);
                    setEditorAttachments([]);
                  }}
                  className="h-5 px-1.5 text-[10px] hover:bg-primary/20 text-primary/70 font-bold"
                >
                  Cancel
                </Button>
              </div>
            )}

            <ConnectedEditor
              key={
                activeMode === 'edit'
                  ? `edit-${editingComment!.id}`
                  : activeMode === 'reply'
                    ? `reply-${replyingToComment!.id}`
                    : 'create'
              }
              mode="message"
              minRows={1}
              value={editingComment?.content || ''}
              attachments={editorAttachments}
              onRemoveAttachment={(idx) => {
                const att = editorAttachments[idx];
                if (att.type === 'remote') {
                  setRemoveAssociationIds((prev) => [...prev, att.associationId]);
                }
                setEditorAttachments((prev) => prev.filter((_, i) => i !== idx));
              }}
              onSubmit={handleSendComment}
              placeholder={
                editingComment
                  ? 'Edit message...'
                  : replyingToComment
                    ? `Reply to ${replyingToComment.author.fullName}...`
                    : 'Type a message...'
              }
              users={users}
              submitLabel={
                isCreatingThread ||
                createCommentMutation.isPending ||
                updateCommentMutation.isPending ||
                isUploading
                  ? editingComment
                    ? 'Saving...'
                    : replyingToComment
                      ? 'Sending...'
                      : 'Sending...'
                  : editingComment
                    ? 'Save Changes'
                    : replyingToComment
                      ? 'Send Reply'
                      : 'Send'
              }
              className={cn(variant === 'overlay' && 'bg-background/80 backdrop-blur')}
            />
          </FileDropzone>
        </div>
      )}
    </div>
  );
}
